/** @file drawableOutline: the selected drawable's tile quad outlined on screen (hovered joint's: thinner). */
import '@babylonjs/core/Meshes/thinInstanceMesh.js';
import { Color3 } from '@babylonjs/core/Maths/math.color.js';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder.js';
import type { Mesh } from '@babylonjs/core/Meshes/mesh.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { Profile } from '@zilo/pose3d-format/types/profile';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import type { Camera, ImpostorList } from '@zilo/pose3d-render-core/contract';
import { IMPOSTOR_LAYOUT as L } from '@zilo/pose3d-render-core/layout/impostorLayout';
import { boneMaterial } from './boneMaterial';
import { displayPoint } from './displayPoint';
import type { V3 } from './matrixRows';
import { quadGeomOf } from './quadGeomOf';
import { segmentQuad } from './segmentQuad';
import type { QuadEdit } from './viewportCtx';
import { VIEWPORT_COLORS } from './viewportColors';
import { VIEWPORT_GROUPS } from './viewportGroups';
import { VIEWPORT_LAYERS } from './viewportLayers';

/** The placed impostors, the outlined drawables (indices in `puppet.drawables`), a running quad drag, the camera, view and `px`. */
export type OutlineInput = {
  list: ImpostorList; count: number; selected: number; hovered: readonly number[]; edit?: QuadEdit | null;
  camera: Camera; profile: Profile; view: View; px: number;
};

/** `update(input)` once per frame (null hides it); `segments()` counts the drawn edges; `mesh` for tests. */
export type DrawableOutline = { mesh: Mesh; update(input: OutlineInput | null): void; segments(): number };

const GOLD = Color3.FromHexString(VIEWPORT_COLORS.hover);
/** An outline's RGBA and its width in CSS px. */
type Look = { edge: readonly number[]; width: number };

/** Selected: 2.5 CSS px, opaque; hovered: 1.5 CSS px at 55 % (T-315 item 11). */
const SELECTED: Look = { edge: [GOLD.r, GOLD.g, GOLD.b, 1], width: 2.5 };
const HOVER: Look = { edge: [GOLD.r, GOLD.g, GOLD.b, 0.55], width: 1.5 };

/** Scratch: an edge's ends and the view's back vector (Babylon frame). */
const P: V3 = [0, 0, 0];
const Q: V3 = [0, 0, 0];
const B: V3 = [0, 0, 0];

/** The look of impostor `i`: selected, hovered or none. */
function lookOf(m: OutlineInput, i: number): Look | null {
  const d = m.list[i * L.stride + L.drawable] ?? -1;
  return d === m.selected ? SELECTED : m.hovered.includes(d) ? HOVER : null;
}

/**
 * Four camera-facing wire segments per outlined impostor along its full tile quad (render-core's `corners`, the quad
 * the runtime draws, a running quad drag applied: `quadGeomOf`), constant CSS width at any zoom, in the marks group of the overlay at full resolution; one
 * thin-instanced quad mesh with the bones' material. Buffers grow (by doubling) only when more edges are needed.
 */
export function drawableOutline(scene: Scene): DrawableOutline {
  const mesh = CreatePlane('viewport-drawable-outline', { size: 1 }, scene);
  Object.assign(mesh, { material: boneMaterial(scene), layerMask: VIEWPORT_LAYERS.overlay, renderingGroupId: VIEWPORT_GROUPS.marks, isPickable: false, alwaysSelectAsActiveMesh: true });
  let buf = { seg: new Float32Array(16 * 16), color: new Float32Array(4 * 16) };
  const bind = (): void => { mesh.thinInstanceSetBuffer('matrix', buf.seg, 16, false); mesh.thinInstanceSetBuffer('color', buf.color, 4, false); };
  bind();
  mesh.thinInstanceCount = 0;
  mesh.setEnabled(false);
  let k = 0;
  const edge = (m: OutlineInput, look: Look, a: [number, number], b: [number, number]): void => {
    if (16 * (k + 1) > buf.seg.length) { buf = { seg: new Float32Array(buf.seg.length * 2), color: new Float32Array(buf.color.length * 2) }; bind(); }
    displayPoint(P, a[0], a[1], m.view);
    displayPoint(Q, b[0], b[1], m.view);
    segmentQuad(buf.seg, 16 * k, P, Q, B, look.width * m.px);
    buf.color.set(look.edge, 4 * k);
    k++;
  };
  return {
    mesh,
    update: (m) => {
      k = 0;
      for (let i = 0; m !== null && i < m.count; i++) {
        const look = lookOf(m, i);
        if (look === null) continue;
        const { back } = m.view.basis;
        [B[0], B[1], B[2]] = [back[0], back[2], back[1]];
        const c = quadGeomOf(m.list, i, m.camera, m.profile, m.edit ?? null).corners;
        for (let e = 0; e < 4; e++) edge(m, look, c[e] ?? c[0], c[(e + 1) % 4] ?? c[0]);
      }
      mesh.setEnabled(k > 0);
      mesh.thinInstanceCount = k;
      if (k > 0) { mesh.thinInstanceBufferUpdated('matrix'); mesh.thinInstanceBufferUpdated('color'); }
    },
    segments: () => k,
  };
}
